import { memo, useEffect, useMemo } from 'react';
import { Handle, Position, useEdges, useNodes, useUpdateNodeInternals } from '@xyflow/react';
import { Zap, Settings, Hash, Tag, CheckCircle2, Trash2, Shuffle, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { FlowEdgeData, FlowNodeData, FlowNodeProps } from '@/types/flow';

const handleStyle = {
    width: '14px',
    height: '14px',
    background: '#2196F3',
    border: '2px solid white',
    borderRadius: '50%',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
} as const;

const TriggerNode = ({ id, data, selected }: FlowNodeProps) => {
    const triggers = data.config?.triggers || [];
    const edges = useEdges();
    const nodes = useNodes();
    const updateNodeInternals = useUpdateNodeInternals();

    // Com sorteio ligado, cada conexão sai da própria linha (handle rand-<transição>),
    // ordenada pela altura do node de destino para as setas cruzarem o mínimo
    const ramos = useMemo(() => {
        if (!data.is_random) return [];
        const lista = edges
            .filter((e) => e.source === id && e.sourceHandle?.startsWith('rand-'))
            .map((e) => {
                const alvo = nodes.find((n) => n.id === e.target);
                return {
                    handleId: e.sourceHandle as string,
                    nome: (alvo?.data as FlowNodeData | undefined)?.node_name || `Node #${e.target}`,
                    y: alvo?.position.y ?? 0,
                    peso: (e.data as FlowEdgeData | undefined)?.weight ?? null,
                };
            })
            .sort((a, b) => a.y - b.y);
        const comPeso = lista.some((r) => r.peso != null);
        const igual = lista.length ? Math.round(100 / lista.length) : 0;
        return lista.map((r) => ({ ...r, pct: comPeso ? (r.peso ?? 0) : igual, estimado: !comPeso }));
    }, [data.is_random, edges, nodes, id]);

    const assinatura = ramos.map((r) => r.handleId).join(',');
    useEffect(() => { updateNodeInternals(id); }, [assinatura, id, updateNodeInternals]);

    const getTriggerIcon = (type: string) => {
        switch (type) {
            case 'keyword': return <Hash className="w-3 h-3" />;
            case 'tag': return <Tag className="w-3 h-3" />;
            case 'exact_match': return <CheckCircle2 className="w-3 h-3" />;
            default: return <Hash className="w-3 h-3" />;
        }
    };

    return (
        <div
            className={`
                bg-white rounded-lg border-l-[3px] border-l-green-500 shadow-sm min-w-[260px] max-w-[300px]
                transition-all duration-150
                ${selected ? 'shadow-md ring-2 ring-blue-400/40' : 'border-t border-r border-b border-gray-200 hover:shadow-md'}
            `}
            style={{ borderTop: selected ? undefined : '1px solid #e5e7eb', borderRight: selected ? undefined : '1px solid #e5e7eb', borderBottom: selected ? undefined : '1px solid #e5e7eb' }}
        >
            {/* Header */}
            <div className="px-3 py-2.5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-green-50 rounded-lg flex items-center justify-center">
                        <Zap className="w-3.5 h-3.5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <span className="font-semibold text-[13px] text-gray-800 block truncate">{data.node_name || 'Gatilho'}</span>
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Passo Inicial</span>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-3 py-2.5">
                {triggers.length === 0 ? (
                    <div className="text-center py-3">
                        <p className="text-xs text-gray-400">Clique para configurar gatilhos</p>
                    </div>
                ) : (
                    <div className="flex flex-wrap gap-1.5">
                        {triggers.slice(0, 5).map((trigger, idx) => (
                            <div
                                key={idx}
                                className="inline-flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-md px-2 py-1"
                            >
                                {getTriggerIcon(trigger.trigger_type)}
                                <span className="text-[11px] text-gray-600 font-medium max-w-[100px] truncate">
                                    {trigger.trigger_value}
                                </span>
                            </div>
                        ))}
                        {triggers.length > 5 && (
                            <span className="text-[11px] text-gray-400 self-center ml-1">+{triggers.length - 5}</span>
                        )}
                    </div>
                )}
            </div>

            {ramos.length > 0 && (
                <div className="px-3 pb-2.5 space-y-1">
                    <div className="flex items-center gap-1.5 text-[10px] text-orange-600 uppercase tracking-wider font-medium pb-0.5">
                        <Shuffle className="w-3 h-3" />
                        Sorteio{ramos[0].estimado ? ' · dividido igual' : ''}
                    </div>
                    {ramos.map((r) => (
                        <div
                            key={r.handleId}
                            className="relative flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-md pl-2 pr-4 py-1.5 min-h-[30px] overflow-visible"
                        >
                            <span className={`text-[11px] font-semibold tabular-nums w-9 ${r.estimado ? 'text-gray-400' : 'text-orange-600'}`}>
                                {r.pct}%
                            </span>
                            <span className="text-[11px] text-gray-700 truncate flex-1" title={r.nome}>{r.nome}</span>
                            <Handle
                                type="source"
                                position={Position.Right}
                                id={r.handleId}
                                isConnectable={false}
                                style={{ ...handleStyle, position: 'absolute', right: '-10px', top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}
                            />
                        </div>
                    ))}
                    <div className="relative flex items-center gap-1.5 rounded-md border border-dashed border-gray-200 pl-2 pr-4 py-1 text-[11px] text-gray-400 overflow-visible">
                        <Plus className="w-3 h-3" />
                        nova conexão
                        <Handle
                            type="source"
                            position={Position.Right}
                            id="source"
                            style={{ ...handleStyle, position: 'absolute', right: '-10px', top: '50%', transform: 'translateY(-50%)', cursor: 'crosshair', zIndex: 10 }}
                        />
                    </div>
                </div>
            )}

            {/* Actions */}
            <div className="px-3 py-1.5 border-t border-gray-100 flex gap-1">
                <Button
                    size="sm"
                    variant="ghost"
                    className="flex-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50/50 h-7 text-[11px]"
                    onClick={() => data.onEdit?.(data.id)}
                >
                    <Settings className="w-3 h-3 mr-1" />
                    Configurar
                </Button>
                {/* Só aparece quando o funil tem mais de um passo inicial (duplicação/import) */}
                {data.canDelete && (
                    <Button
                        size="sm"
                        variant="ghost"
                        className="text-gray-400 hover:text-red-500 hover:bg-red-50/50 h-7 px-2"
                        title="Excluir passo inicial duplicado"
                        onClick={(e) => { e.stopPropagation(); data.onDelete?.(data.id); }}
                    >
                        <Trash2 className="w-3 h-3" />
                    </Button>
                )}
            </div>

            {ramos.length === 0 && (
                <Handle
                    type="source"
                    position={Position.Right}
                    id="source"
                    className="!w-3.5 !h-3.5 !bg-[#2196F3] !border-2 !border-white"
                    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                />
            )}
        </div>
    );
};

export default memo(TriggerNode);
